import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { WizardComponent, WizardStepComponent } from 'mbs-ui-kit';
import { InstallationMode, getOsTypes } from 'mbs-ui-shared@components/add-computer/add-computer.constants';
import { WizardForm } from 'mbs-ui-shared@components/add-computer/add-computer.types';
import { BrandsFacade } from 'mbs-ui-shared@facades/brands.facade';
import { BuildsFacade } from 'mbs-ui-shared@facades/builds.facade';
import Brand from 'mbs-ui-shared@models/Brand';
import { AgentType } from 'mbs-ui-shared@models/Computer';
import { Build, BuildOsType, getSupportCompanyMinimalVersion } from 'mbs-ui-shared@models/build';
import { getNormalizedVersion } from 'mbs-ui-shared@utils/get-normalized-version';
import { Observable, filter } from 'rxjs';
import { tap } from 'rxjs/operators';

import { DownloadAndInstallComponent } from 'mbs-ui-shared@components/add-computer/steps/download-and-install/download-and-install.component';
import { ReadManualComponent } from 'mbs-ui-shared@components/add-computer/steps/read-manual/read-manual.component';
import { SelectActionComponent } from 'mbs-ui-shared@components/add-computer/steps/select-action/select-action.component';
import { BuildType, Company } from 'mbs-ui-shared@models';

@UntilDestroy()
@Component({
  selector: 'add-computer-wizard',
  templateUrl: './add-computer.component.html',

  imports: [I18NextPipe, WizardStepComponent, DownloadAndInstallComponent, ReadManualComponent, SelectActionComponent, WizardComponent]
})
export class AddComputerComponent implements OnInit {
  private fb = inject(FormBuilder);
  private brandsFacade = inject(BrandsFacade);
  private buildsFacade = inject(BuildsFacade);
  private i18N = inject(I18NextPipe);

  public readonly agentType = AgentType;
  public readonly location = location;

  public mode: AgentType.Backup | AgentType.RMM | AgentType.RA;
  public loading = true;
  public availableProducts: Set<AgentType> = new Set();
  public osTypes = getOsTypes(this.i18N);
  public availableOsTypes = this.osTypes;
  public wizardForm: WizardForm = this.fb.group({
    os: [BuildOsType.Windows, [Validators.required]],
    agentType: [null as AgentType, [Validators.required]],
    companyId: [null as Company['id']],
    mode: [InstallationMode.Manual]
  });
  public builds: Build[];
  public displayedBuilds: Build[];
  public licensedBrandList: Brand[];
  public showPendingFilterButton = true;
  public networkDiscoveryBuild: Build = null;
  public showCompanyUnsupportedAlert = false;

  @ViewChild('downloadStep', { static: true }) downloadStep;
  @ViewChild(WizardComponent, { static: true }) wizard: WizardComponent;

  ngOnInit() {
    this.initBrands().subscribe();

    this.buildsFacade.builds$.pipe(untilDestroyed(this), filter(Boolean)).subscribe((builds) => {
      this.builds = builds;
      this.networkDiscoveryBuild = builds.find(({ mbsBuildType }) => mbsBuildType === BuildType.NetworkDiscovery);
      this.updateDisplayedBuilds();

      if (this.loading) {
        this.setAvailableFormValues();
        this.initFormSubscriptions();
        this.setDefaultFormValues();
        this.loading = false;
      }
    });
  }

  public updateDisplayedBuilds(): void {
    const { agentType, os } = this.wizardForm.value;
    this.displayedBuilds = this.getDisplayedBuilds({ builds: this.builds, agentType, os });

    this.showCompanyUnsupportedAlert = !this.displayedBuilds.some((build) => {
      const isPublicVersionSupportCompany =
        getNormalizedVersion(build?.public?.version) >= getSupportCompanyMinimalVersion(build.mbsBuildType);
      const isSandboxVersionSupportCompany =
        getNormalizedVersion(build?.sandbox?.version) >= getSupportCompanyMinimalVersion(build.mbsBuildType);

      return isPublicVersionSupportCompany || isSandboxVersionSupportCompany;
    });
  }

  public getDisplayedBuilds({ builds, agentType, os }): Build[] {
    return builds.filter((build) => {
      return build.applicationId === agentType && build.buildOs === os;
    });
  }

  private initBrands(): Observable<Brand[]> {
    return this.brandsFacade.getActive$().pipe(
      tap((brandList) => {
        this.licensedBrandList = brandList;
      })
    );
  }

  private initFormSubscriptions(): void {
    this.wizardForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.updateDisplayedBuilds();
    });

    this.wizardForm
      .get('agentType')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((agentType) => {
        this.availableOsTypes =
          agentType === AgentType.RA
            ? this.osTypes.filter((type) => type.id === BuildOsType.Windows || type.id === BuildOsType.Mac)
            : this.osTypes;
        agentType === AgentType.RA && this.wizardForm.get('os').patchValue(BuildOsType.Windows);
      });
  }

  private setAvailableFormValues(): void {
    this.builds.forEach((build) => {
      this.availableProducts.add(build.applicationId as AgentType);
    });
  }

  private setDefaultFormValues(): void {
    this.wizardForm.get('agentType').patchValue(this.getDefaultAgentType());
  }

  private getDefaultAgentType(): AgentType {
    if (this.mode === AgentType.Backup) {
      return AgentType.Backup;
    }

    if (this.mode === AgentType.RMM) {
      return AgentType.RMM;
    }

    return AgentType.RA;
  }
}
